import { Box, Button, Container, Stack, Typography } from '@locus-taxy/encore-mui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../utils/testIdUtils';
import { useProfilePage } from './UserDetails/useProfilePage';
import { ProfileHeaderCard } from './UserDetails/ProfileHeaderCard';
import { PersonalInfoSection } from './UserDetails/PersonalInfoSection';
import { LocaleSettingsSection } from './UserDetails/LocaleSettingsSection';
import { WorkProfileSection } from './UserDetails/WorkProfileSection';
import { SessionAccessSection } from './UserDetails/SessionAccessSection';
import { UnsavedChangesDialog } from './UserDetails/UnsavedChangesDialog';

export default function UserDetails() {
  const { t } = useTranslation();
  const {
    mode, isDirty, isSaving, showUnsavedDialog, canUpdate, clientId,
    fullName, entityData, configuredTimezone, configuredLocale,
    timezoneOptions, localeOptions, lastUpdated,
    photoUrl, photoLoading, hasPhoto,
    workProfileData, personalInfoViewFields, localeViewFields,
    personalInfoFormRef, localeFormRef,
    handleEditClick, handleCancel, handleSave,
    handleDialogStay, handleDialogLeave,
    handlePersonalInfoChange, handleLocaleChange,
    handlePhotoSelect, handlePhotoRemove, handleLogout,
  } = useProfilePage();

  return (
    <Stack
      height="100%"
      overflow="hidden"
      data-testid={getTestId({ action: TestIdAction.VIEW, object: 'account-page-container', page: 'account' })}
    >
      {/* TitleBar */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        padding="24px 48px"
        bgcolor="background.paper"
        flexShrink={0}
        data-testid={getTestId({ action: TestIdAction.VIEW, object: 'profile-title-bar', page: 'account' })}
      >
        <Stack spacing={0.5}>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            {t('userDetails.profileSettings', 'Profile Settings')}
          </Typography>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              {t('userDetails.lastUpdated', 'Last updated: {{date}}', { date: lastUpdated })}
            </Typography>
          )}
        </Stack>

        {mode === 'view' ? (
          <Button
            variant="contained" size="large" color="primary"
            disabled={!canUpdate} onClick={handleEditClick}
            data-testid={getTestId({ action: TestIdAction.VIEW, object: 'edit-profile-button', page: 'account' })}
          >
            {t('userDetails.editProfile', 'Edit Profile')}
          </Button>
        ) : (
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined" size="large" color="inherit"
              disabled={isSaving} onClick={handleCancel}
              data-testid={getTestId({ action: TestIdAction.VIEW, object: 'cancel-button', page: 'account' })}
            >
              {t('userDetails.cancel', 'Cancel')}
            </Button>
            <Button
              variant="contained" size="large" color="primary"
              disabled={isSaving || !isDirty} onClick={handleSave}
              data-testid={getTestId({ action: TestIdAction.VIEW, object: 'save-changes-button', page: 'account' })}
            >
              {t('userDetails.saveChanges', 'Save Changes')}
            </Button>
          </Stack>
        )}
      </Stack>

      {/* Scrollable content */}
      <Box flex={1} overflow="auto" bgcolor="#f6f7f9" padding="32px 0 48px">
        <Container maxWidth="md">
          <Stack spacing={3}>
          <ProfileHeaderCard
            mode={mode} fullName={fullName}
            designation={entityData?.profileInformation?.emailAddress as string | undefined}
            roleName={workProfileData.roleName || undefined}
            photoUrl={photoUrl} photoLoading={photoLoading}
            onPhotoSelect={handlePhotoSelect} onPhotoRemove={handlePhotoRemove}
            hasPhoto={hasPhoto}
          />
          <PersonalInfoSection
            mode={mode} entityData={entityData as Record<string, unknown> | undefined}
            clientId={clientId} formRef={personalInfoFormRef}
            onChange={handlePersonalInfoChange} viewFields={personalInfoViewFields}
          />
          <LocaleSettingsSection
            mode={mode} clientId={clientId} formRef={localeFormRef}
            onChange={handleLocaleChange} timezone={configuredTimezone}
            timezoneOptions={timezoneOptions} locale={configuredLocale}
            localeOptions={localeOptions} viewFields={localeViewFields}
          />
          <WorkProfileSection
            roleName={workProfileData.roleName} teamNames={workProfileData.teamNames}
            skillNames={workProfileData.skillNames} joiningDate={workProfileData.joiningDate}
            locale={configuredLocale}
          />
          <SessionAccessSection onLogout={handleLogout} />
          </Stack>
        </Container>
      </Box>

      <UnsavedChangesDialog open={showUnsavedDialog} onStay={handleDialogStay} onLeave={handleDialogLeave} />
    </Stack>
  );
}
